import { useEffect, useState } from "react";
import { apiClient } from "../api/client.js";
import {
  pollInboxUntilDone,
  type Snapshot,
} from "../components/asset-history-tab.js";
import { FolderRestoreModal } from "../components/folder-restore-modal.js";
import { SnapshotDeltaSummary } from "../components/snapshot-delta-summary.js";
import {
  SnapshotTimeSlider,
  type SnapshotForSlider,
} from "../components/snapshot-time-slider.js";

// 3.B.3.1: /historia-pääsivu.
//
// V1: Lataa kaikki snapshotit kaikilta source-rooteilta daemon-inbox:n
// snapshot-list -handlerilla. Esittää aikajakson time-sliderissa →
// käyttäjä voi valita snapshotin → "Palauta tämä snapshot" -CTA avaa
// folder-restore-modaalin.
//
// Tree-view alipuun selaamisesta tulee myöhemmin — vaatii uuden
// snapshot-ls inbox-handlerin per-hakemisto-listausta varten. V1:ssä
// käyttäjä voi syöttää sourcePath:n manuaalisesti restore-modaaliin.

const SNAPSHOT_LIST_TIMEOUT_MS = 60_000;

export function Historia() {
  const [machineId, setMachineId] = useState<string | null>(null);
  const [machinesLoading, setMachinesLoading] = useState(true);
  const [snapshots, setSnapshots] = useState<Snapshot[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [restoreOpen, setRestoreOpen] = useState(false);

  // Hae machineId
  useEffect(() => {
    let cancelled = false;
    apiClient
      .listMachines()
      .then((res) => {
        if (cancelled) return;
        const first = res.machines[0];
        setMachineId(first?.id ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setMachinesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Hae snapshot-lista
  useEffect(() => {
    if (!machineId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const created = await apiClient.postInboxRequest(machineId, {
          type: "snapshot-list",
          request: {},
        });
        const result = await pollInboxUntilDone(created.id, SNAPSHOT_LIST_TIMEOUT_MS);
        if (cancelled) return;
        if (result.status === "completed") {
          const raw =
            (result.result as { snapshots?: Snapshot[] } | null)?.snapshots ?? [];
          // Järjestys: vanhin → uusin (slider:in oikea reuna = uusin)
          const sorted = [...raw].sort((a, b) =>
            a.startTime.localeCompare(b.startTime)
          );
          setSnapshots(sorted);
          setSelectedIndex(sorted.length > 0 ? sorted.length - 1 : 0);
        } else {
          setError(result.error ?? `Snapshot-haku ${result.status}`);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [machineId]);

  const selectedSnapshot = snapshots ? snapshots[selectedIndex] ?? null : null;

  return (
    <section className="mx-auto max-w-4xl px-6 py-10">
      <header className="mb-6">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-50">
          Snapshot-historia
        </h2>
        <p className="mt-2 text-sm text-slate-400">
          Selaa kopia-snapshotteja ajassa ja palauta kansioita tai tiedostoja
          haluamastasi hetkestä.
        </p>
      </header>

      {machinesLoading && <p className="text-slate-500">Etsitään daemonia…</p>}
      {!machinesLoading && !machineId && (
        <p className="rounded-md border border-yellow-700 bg-yellow-950/40 px-3 py-2 text-sm text-yellow-200">
          Ei daemonia kytkettynä. Avaa /levyt-sivu ja varmista että Macin
          daemon näkyy.
        </p>
      )}
      {loading && <p className="text-slate-500">Haetaan snapshot-listaa Macilta…</p>}
      {error && (
        <p className="rounded-md border border-red-700 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}
      {snapshots && snapshots.length > 0 && (
        <div className="space-y-4">
          <SnapshotTimeSlider
            snapshots={snapshots as SnapshotForSlider[]}
            selectedIndex={selectedIndex}
            onSelect={setSelectedIndex}
          />
          {selectedSnapshot && machineId && snapshots.length > 0 && (
            <div className="space-y-1">
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Erot uusimpaan snapshot:iin
              </h3>
              <SnapshotDeltaSummary
                machineId={machineId}
                selectedSnapshotId={selectedSnapshot.id}
                baselineSnapshotId={snapshots[snapshots.length - 1]!.id}
              />
            </div>
          )}
          {selectedSnapshot && machineId && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setRestoreOpen(true)}
                className="rounded bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600"
              >
                Palauta tämä snapshot
              </button>
            </div>
          )}
        </div>
      )}
      {snapshots && snapshots.length === 0 && !loading && (
        <p className="text-slate-500">Ei vielä snapshotteja.</p>
      )}

      {restoreOpen && selectedSnapshot && machineId && snapshots && (
        <FolderRestoreModal
          snapshot={selectedSnapshot}
          machineId={machineId}
          baselineSnapshotId={snapshots[snapshots.length - 1]!.id}
          onClose={() => setRestoreOpen(false)}
        />
      )}
    </section>
  );
}
